import { ClientPrincipal } from './auth';
import { API_URL, getDabUrl } from './api';

export interface TravelFormServer {
  id?: number;
  owner_user_id?: string;
  owner_email?: string | null;
  owner_name?: string | null;
  project_number: string;
  project_name?: string | null;
  client?: string | null;
  team_leader?: string | null;
  duty_officer?: string | null;
  travel_date_from: string;
  travel_date_to: string;
  client_contact_name?: string | null;
  client_contact_email?: string | null;
  client_contact_phone?: string | null;
  fcdo_restrictions?: string | null;
  status: 'draft' | 'complete' | 'submitted' | 'approved';
  created_at?: string;
  last_modified?: string;

  // Pre-Deployment Checks
  bamboo_updated?: string | null; // Y/N
  pre_deployment_brief?: string | null; // Y/N
  happy_to_travel_location?: string | null; // Y/N
  happy_to_travel_companions?: string | null; // Y/N
  safe_heat_training?: string | null; // Y/N
  security_clearance?: string | null; // Y/N or text
  medical_clearance?: string | null; // Y/N
  visa_obtained?: string | null; // Y/N
  doctor_advised_not_travel?: string | null; // Y/N
  pre_existing_condition_understood?: string | null; // Y/N

  // Contact & Comms
  whatsapp_phone?: string | null;
  focal_in_country_name?: string | null;
  focal_in_country_contact?: string | null;
  client_contact_name_alt?: string | null;
  client_contact_details_alt?: string | null;
  travelling_with_iod?: string | null; // Y/N
  travelling_with_name?: string | null;

  // Travel & Logistics
  airport_transfers_arranged?: string | null; // Y/N
  in_country_transport?: string | null;
  itinerary_forwarded?: string | null; // Y/N or text

  // Travel Type
  travel_type?: 'project' | 'travel';
  travel_reason?: string | null;
}

export interface AccommodationServer {
  id?: number;
  travel_form_id: number;
  check_in_date: string;
  check_out_date: string;
  location?: string | null;
  hotel_name?: string | null;
  address?: string | null;
  phone?: string | null;
  nights?: number | null;
  sort_order?: number;
}

export interface TravelFormClient {
  formId?: number;
  projectNumber: string;
  projectName: string;
  client: string;
  teamLeader: string;
  dutyOfficer: string;
  travelDateFrom: string;
  travelDateTo: string;
  countries: string[];
  clientContactName: string;
  clientContactEmail: string;
  clientContactPhone: string;
  fcdoRestrictions: string;
  accommodations: Array<{
    clientId: string;
    serverId?: number;
    checkInDate: string;
    checkOutDate: string;
    location: string;
    hotelName: string;
    address: string;
    phone: string;
    nights: number;
  }>;
  travelSegments: any[];
  status: 'draft' | 'complete' | 'submitted' | 'approved';
  lastSaved: string;

  // Pre-Deployment Checks
  bambooUpdated?: string;
  preDeploymentBrief?: string;
  happyToTravelLocation?: string;
  happyToTravelCompanions?: string;
  safeHeatTraining?: string;
  securityClearance?: string;
  medicalClearance?: string;
  visaObtained?: string;
  doctorAdvisedNotTravel?: string;
  preExistingConditionUnderstood?: string;

  // Contact & Comms
  whatsappPhone?: string;
  focalInCountryName?: string;
  focalInCountryContact?: string;
  clientContactNameAlt?: string;
  clientContactDetailsAlt?: string;
  travellingWithIod?: string;
  travellingWithName?: string;

  // Travel & Logistics
  airportTransfersArranged?: string;
  inCountryTransport?: string;
  itineraryForwarded?: string;

  // Travel Type
  travelType?: 'project' | 'travel';
  travelReason?: string;
}

/**
 * Extract user email from principal, prioritizing email claims over userDetails
 * (userDetails might be a GUID instead of an email)
 */
export function extractUserEmail(principal: ClientPrincipal): string {
  const emailClaim = principal.claims?.find(c =>
    c.typ?.toLowerCase().includes('email') ||
    c.typ?.toLowerCase().includes('upn') ||
    c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
  )?.val;

  return emailClaim || principal.userDetails || '';
}

export function toServerForm(client: TravelFormClient, principal: ClientPrincipal, existingId?: number): TravelFormServer {
  const userEmail = extractUserEmail(principal);
  const ownerName = principal.name || null;

  // Format dates as YYYY-MM-DD for SQL
  const formatDate = (dateStr: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };

  // Note: Don't send 'id' for new records - database will auto-generate
  const payload: any = {
    owner_user_id: userEmail,
    owner_email: userEmail,
    owner_name: ownerName,
    project_number: client.projectNumber || '',
    project_name: client.projectName || null,
    client: client.client || null,
    team_leader: client.teamLeader || null,
    duty_officer: client.dutyOfficer || null,
    client_contact_name: client.clientContactName || null,
    client_contact_email: client.clientContactEmail || null,
    client_contact_phone: client.clientContactPhone || null,
    fcdo_restrictions: client.fcdoRestrictions || null,
    status: client.status,

    // Pre-Deployment Checks
    bamboo_updated: client.bambooUpdated || null,
    pre_deployment_brief: client.preDeploymentBrief || null,
    happy_to_travel_location: client.happyToTravelLocation || null,
    happy_to_travel_companions: client.happyToTravelCompanions || null,
    safe_heat_training: client.safeHeatTraining || null,
    security_clearance: client.securityClearance || null,
    medical_clearance: client.medicalClearance || null,
    visa_obtained: client.visaObtained || null,
    doctor_advised_not_travel: client.doctorAdvisedNotTravel || null,
    pre_existing_condition_understood: client.preExistingConditionUnderstood || null,

    // Contact & Comms
    whatsapp_phone: client.whatsappPhone || null,
    focal_in_country_name: client.focalInCountryName || null,
    focal_in_country_contact: client.focalInCountryContact || null,
    client_contact_name_alt: client.clientContactNameAlt || null,
    client_contact_details_alt: client.clientContactDetailsAlt || null,
    travelling_with_iod: client.travellingWithIod || null,
    travelling_with_name: client.travellingWithName || null,

    // Travel & Logistics
    airport_transfers_arranged: client.airportTransfersArranged || null,
    in_country_transport: client.inCountryTransport || null,
    itinerary_forwarded: client.itineraryForwarded || null,

    // Travel Type
    travel_type: client.travelType || 'project',
    travel_reason: client.travelReason || null,
  };

  // Only include date fields if they have values (DAB rejects null for date types)
  const dateFrom = formatDate(client.travelDateFrom);
  const dateTo = formatDate(client.travelDateTo);
  if (dateFrom) payload.travel_date_from = dateFrom;
  if (dateTo) payload.travel_date_to = dateTo;

  // Only include id for updates
  if (existingId) {
    payload.id = existingId;
  }

  return payload;
}

export function fromServerForm(server: TravelFormServer): Partial<TravelFormClient> {
  // Format dates for HTML date inputs (YYYY-MM-DD only)
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  return {
    formId: server.id,
    projectNumber: server.project_number || '',
    projectName: server.project_name || '',
    client: server.client || '',
    teamLeader: server.team_leader || '',
    dutyOfficer: server.duty_officer || '',
    travelDateFrom: formatDate(server.travel_date_from || ''),
    travelDateTo: formatDate(server.travel_date_to || ''),
    clientContactName: server.client_contact_name || '',
    clientContactEmail: server.client_contact_email || '',
    clientContactPhone: server.client_contact_phone || '',
    fcdoRestrictions: server.fcdo_restrictions || '',
    status: server.status,

    // Pre-Deployment Checks
    bambooUpdated: server.bamboo_updated || '',
    preDeploymentBrief: server.pre_deployment_brief || '',
    happyToTravelLocation: server.happy_to_travel_location || '',
    happyToTravelCompanions: server.happy_to_travel_companions || '',
    safeHeatTraining: server.safe_heat_training || '',
    securityClearance: server.security_clearance || '',
    medicalClearance: server.medical_clearance || '',
    visaObtained: server.visa_obtained || '',
    doctorAdvisedNotTravel: server.doctor_advised_not_travel || '',
    preExistingConditionUnderstood: server.pre_existing_condition_understood || '',

    // Contact & Comms
    whatsappPhone: server.whatsapp_phone || '',
    focalInCountryName: server.focal_in_country_name || '',
    focalInCountryContact: server.focal_in_country_contact || '',
    clientContactNameAlt: server.client_contact_name_alt || '',
    clientContactDetailsAlt: server.client_contact_details_alt || '',
    travellingWithIod: server.travelling_with_iod || '',
    travellingWithName: server.travelling_with_name || '',

    // Travel & Logistics
    airportTransfersArranged: server.airport_transfers_arranged || '',
    inCountryTransport: server.in_country_transport || '',
    itineraryForwarded: server.itinerary_forwarded || '',

    // Travel Type
    travelType: server.travel_type || 'project',
    travelReason: server.travel_reason || '',
  } as Partial<TravelFormClient>;
}

function odataStringLiteral(value: string): string {
  const safe = (value || '').replace(/'/g, "''");
  return `'${safe}'`;
}

export async function fetchDraftFormFor(principal: ClientPrincipal): Promise<TravelFormServer | null> {
  const userEmail = extractUserEmail(principal);
  // Fetch all forms and filter client-side
  const res = await fetch(getDabUrl('TravelForms'), {
    credentials: 'include'
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  const items: TravelFormServer[] = data?.value || data || [];

  // Filter for user's draft forms
  const userDrafts = items.filter(f =>
    f.status === 'draft' && (f.owner_user_id === userEmail || f.owner_email === userEmail)
  );

  return userDrafts[0] || null;
}

export async function fetchFormById(id: number): Promise<TravelFormServer | null> {
  const res = await fetch(getDabUrl('GetTravel'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_id: id }),
    credentials: 'include'
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.value?.[0] || data;
}

export async function upsertDraftForm(clientForm: TravelFormClient, principal: ClientPrincipal): Promise<TravelFormServer> {
  const body = toServerForm(clientForm, principal, clientForm.formId);
  console.log('🔍 Upserting form with email:', body.owner_email, 'user_id:', body.owner_user_id);
  if (clientForm.formId) {
    // Use stored procedure for update
    console.log('🔍 Updating existing form ID:', clientForm.formId);
    const res = await fetch(getDabUrl('UpdateTravel'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        id: clientForm.formId,
        owner_user_id: body.owner_user_id,
        owner_email: body.owner_email,
        owner_name: body.owner_name,
        project_number: body.project_number,
        project_name: body.project_name,
        client: body.client,
        team_leader: body.team_leader || null,
        duty_officer: body.duty_officer || null,
        travel_date_from: body.travel_date_from || null,
        travel_date_to: body.travel_date_to || null,
        client_contact_name: body.client_contact_name || null,
        client_contact_email: body.client_contact_email || null,
        client_contact_phone: body.client_contact_phone || null,
        fcdo_restrictions: body.fcdo_restrictions || null,
        status: body.status || 'draft',

        // Pre-Deployment Checks
        bamboo_updated: body.bamboo_updated || null,
        pre_deployment_brief: body.pre_deployment_brief || null,
        happy_to_travel_location: body.happy_to_travel_location || null,
        happy_to_travel_companions: body.happy_to_travel_companions || null,
        safe_heat_training: body.safe_heat_training || null,
        security_clearance: body.security_clearance || null,
        medical_clearance: body.medical_clearance || null,
        visa_obtained: body.visa_obtained || null,
        doctor_advised_not_travel: body.doctor_advised_not_travel || null,
        pre_existing_condition_understood: body.pre_existing_condition_understood || null,

        // Contact & Comms
        whatsapp_phone: body.whatsapp_phone || null,
        focal_in_country_name: body.focal_in_country_name || null,
        focal_in_country_contact: body.focal_in_country_contact || null,
        client_contact_name_alt: body.client_contact_name_alt || null,
        client_contact_details_alt: body.client_contact_details_alt || null,
        travelling_with_iod: body.travelling_with_iod || null,
        travelling_with_name: body.travelling_with_name || null,

        // Travel & Logistics
        airport_transfers_arranged: body.airport_transfers_arranged || null,
        in_country_transport: body.in_country_transport || null,
        itinerary_forwarded: body.itinerary_forwarded || null,

        // Travel Type
        travel_type: body.travel_type || 'project'
      }),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Update failed:', res.status, errorText);
      throw new Error(`Update failed: ${res.status}`);
    }
    const data = await res.json();
    return data?.value?.[0] || data;
  } else {
    console.log('Creating new form with body:', body);
    const res = await fetch(getDabUrl('TravelForms'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Create failed:', res.status, errorText);
      throw new Error(`Create failed: ${res.status} - ${errorText}`);
    }
    const data = await res.json();
    console.log('Create response:', data);
    // DAB returns {value: [form]} for POST, extract the form
    return data?.value?.[0] || data;
  }
}

export async function fetchAccommodations(formId: number): Promise<AccommodationServer[]> {
  const res = await fetch(getDabUrl(`TravelAccommodations?$filter=travel_form_id eq ${formId}&$orderby=sort_order asc,id asc`), {
    credentials: 'include'
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => null);
  return (data?.value || data || []) as AccommodationServer[];
}

export async function fetchCountries(formId: number): Promise<string[]> {
  const res = await fetch(getDabUrl(`TravelFormCountries?$filter=travel_form_id eq ${formId}&$orderby=sort_order asc`), {
    credentials: 'include'
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => null);
  const items = data?.value || data || [];
  return items.map((item: any) => item.country_code);
}

export async function fetchSegments(formId: number): Promise<any[]> {
  const res = await fetch(getDabUrl(`TravelSegments?$filter=travel_form_id eq ${formId}&$orderby=sort_order asc`), {
    credentials: 'include'
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => null);
  return data?.value || data || [];
}

// REMOVED: upsertAccommodation and deleteAccommodation
// These used PATCH/DELETE with ID which DAB doesn't support
// Use saveAccommodations instead (batch delete + recreate)

export async function saveCountries(formId: number, countryCodes: string[]): Promise<void> {
  // Delete existing countries using stored procedure
  await fetch(getDabUrl('DeleteFormChildren'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_id: formId, child_type: 'countries' }),
    credentials: 'include'
  });

  // Insert new countries
  for (let i = 0; i < countryCodes.length; i++) {
    const code = countryCodes[i];
    await fetch(getDabUrl('TravelFormCountries'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        travel_form_id: formId,
        country_code: code,
        country_name: code, // You might want to map this to full name
        sort_order: i
      })
    });
  }
}

export async function saveSegments(formId: number, segments: any[]): Promise<void> {
  // Delete existing segments using stored procedure
  await fetch(getDabUrl('DeleteFormChildren'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_id: formId, child_type: 'segments' }),
    credentials: 'include'
  });

  // Format date helper
  const formatDate = (dateStr: string) => {
    if (!dateStr) return null;
    return dateStr.split('T')[0]; // Extract YYYY-MM-DD
  };

  // Insert new segments
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    await fetch(getDabUrl('TravelSegments'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        travel_form_id: formId,
        travel_date: formatDate(seg.travelDate),
        travel_type: seg.travelType,
        flight_number: seg.flightNumber || null,
        route_from: seg.routeFrom || null,
        route_to: seg.routeTo || null,
        departure_time: seg.departureTime || null,
        arrival_time: seg.arrivalTime || null,
        booking_ref: seg.bookingRef || null,
        sort_order: i
      })
    });
  }
}

export async function saveAccommodations(formId: number, accommodations: any[]): Promise<void> {
  // Delete existing accommodations using stored procedure
  const deleteRes = await fetch(getDabUrl('DeleteFormChildren'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_id: formId, child_type: 'accommodations' }),
    credentials: 'include'
  });

  if (!deleteRes.ok) {
    console.error('Failed to delete accommodations:', deleteRes.status, await deleteRes.text());
    throw new Error(`Failed to delete accommodations: ${deleteRes.status}`);
  }

  // Format date helper
  const formatDate = (dateStr: string) => {
    if (!dateStr) return null;
    return dateStr.split('T')[0]; // Extract YYYY-MM-DD
  };

  // Insert new accommodations
  for (let i = 0; i < accommodations.length; i++) {
    const acc = accommodations[i];
    await fetch(getDabUrl('TravelAccommodations'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        travel_form_id: formId,
        check_in_date: formatDate(acc.checkInDate),
        check_out_date: formatDate(acc.checkOutDate),
        location: acc.location || null,
        hotel_name: acc.hotelName || null,
        address: acc.address || null,
        phone: acc.phone || null,
        nights: acc.nights || null,
        sort_order: i
      })
    });
  }
}

export async function deleteForm(id: number, principal: ClientPrincipal): Promise<void> {
  // First delete child records (accommodations, countries, segments)
  await fetch(getDabUrl('DeleteFormChildren'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_id: id, child_type: 'accommodations' }),
    credentials: 'include'
  });
  
  await fetch(getDabUrl('DeleteFormChildren'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_id: id, child_type: 'countries' }),
    credentials: 'include'
  });
  
  await fetch(getDabUrl('DeleteFormChildren'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form_id: id, child_type: 'segments' }),
    credentials: 'include'
  });
  
  // Now delete the main form using DAB entity endpoint with item ID
  // DAB requires DELETE to /EntityName/id/{id} format
  const res = await fetch(getDabUrl(`TravelForms/id/${id}`), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  });
  
  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    console.error('Delete form failed:', res.status, errorText);
    throw new Error(`Form delete failed: ${res.status}`);
  }
}


export async function duplicateForm(id: number, principal: ClientPrincipal): Promise<TravelFormServer> {
  const original = await fetchFormById(id);
  if (!original) throw new Error('Original form not found');

  const userEmail = extractUserEmail(principal);

  const newForm: TravelFormServer = {
    owner_user_id: userEmail,
    owner_email: userEmail,
    owner_name: principal.name || null,
    project_number: original.project_number,
    project_name: original.project_name || null,
    client: original.client || null,
    travel_date_from: original.travel_date_from,
    travel_date_to: original.travel_date_to,
    client_contact_name: original.client_contact_name || null,
    client_contact_email: original.client_contact_email || null,
    client_contact_phone: original.client_contact_phone || null,
    fcdo_restrictions: original.fcdo_restrictions || null,
    travel_type: original.travel_type || 'project',
    status: 'draft'
  };

  const created = await (await fetch(getDabUrl('TravelForms'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(newForm)
  })).json();

  return created;
}
